import { _ } from "../../../../core/polyglot/polyglot.js";
import { create } from "../../../../core/helpers/pdf-util.js";
import {
	fileAltIcon,
	filePdfIcon,
} from "../../../../core/elements/icons/index.js";
import accountRowVisibilityFactory from "../../../../models/reports/accountRowVisibilities/accountRowVisibilityFactory.js";
import PageOverlayWidget from "../../../../core/widgets/PageOverlayWidget.js";
import PdfExportingVisitor from "../../../reports2/visitors/exporters/PdfExportingVisitor.js";
import PeriodReportPDFExporter from "./PeriodReportPDFExporter.js";
import ReportDisclaimer from "../../../../models/ReportDisclaimer.js";
import ReportService from "../../../reports2/ReportService.js";
import session from "../../../../core/session.js";

/**
 * Export a period report to a PDF file.
 *
 * The export service exports a period report using a `PeriodReportPDFExporter`,
 * and appends extra pages for the balance sheet and income statement.
 *
 * @param {periodReport} [spec.periodReport] - periodPeriod to be exported.
 * @param {ChartOfAccounts} [chartOfAccounts] - Used by the ReportService when
 *   making attachments.
 * @param {KeyFigure[]} [keyFigures] - Used by the ReportService when making
 *   attachments.
 * @param {Renderable} [overlayContent] - the overlay content to render that
 *   hides the period report during the PDF export. By default, it renders a
 *   fileAltIcon and filePdfIcon seperated by an arrow and the sentence:
 *   "Generating the PDF file, please wait…"
 */
export default class PeriodReportPDFExportService {
	constructor({ periodReport, chartOfAccounts, keyFigures, overlayContent }) {
		this._periodReport = periodReport;
		this._reportTemplateIds = ["income-statement", "balance-sheet"];
		this._chartOfAccounts = chartOfAccounts;
		this._keyFigures = keyFigures;

		this._overlayContent =
			overlayContent ?? this._renderDefaultOverlayContentOn;
	}

	export(callback) {
		// FTGP-8844, FTGP-8870: Stitching is temporarily disabled
		// this._exportReportAttachments(reportsData => {
		// 	this._export(data => {
		// 		callback(this._mergePdfData([data, ...reportsData]));
		// 	});
		// });
		const overlay = this._makeOverlay();
		overlay.show();

		setTimeout(() => {
			this._exportPeriodReport().then((pdf) => {
				callback(pdf);

				// To avoid visual glitches with the viewport resizing after the
				// export to PDF, wait a bit before hiding the overlay so the
				// DOM has time to perform the reflow.
				setTimeout(overlay.hide.bind(overlay), 500);
			});
		}, 400);
	}

	reportHasAllDefaultAttachments() {
		const defaultAttachments = this._periodReport.defaultAttachments();

		return defaultAttachments.length === this._reportTemplateIds.length;
	}

	download(filename) {
		this.export((data) => {
			data.download(filename).catch(() => {});
		});
	}

	async buildReportAttachments({
		reportTemplateIds = this._reportTemplateIds,
	}) {
		const data = await this._exportReportAttachments(reportTemplateIds);
		return data.map((datum) => this._buildPDF(datum));
	}

	async _exportPeriodReport() {
		const exporter = new PeriodReportPDFExporter({
			reportDisclaimerTemplateVariables:
				this._reportDisclaimerTemplateVariables(),
			periodReport: this._periodReport,
		});

		const data = await exporter.export();

		return this._buildPDF(data);
	}

	_buildPDF(data) {
		const pdf = create(data);
		pdf.title = data.title;
		return pdf;
	}

	async _exportReportAttachments(reportTemplateIds) {
		const reports = await this._makeReportAttachments(reportTemplateIds);

		return reports.map((report) =>
			new PdfExportingVisitor({
				company: session.getCompany(),
				reportDisclaimerTemplateVariables:
					this._reportDisclaimerTemplateVariables(),
			}).visit(report),
		);
	}

	_reportDisclaimerTemplateVariables() {
		return ReportDisclaimer.buildTemplateVariables({
			agency: session.agency,
			company: session.getCompany(),
			currentPeriod: session.getPeriod(),
		});
	}

	_makeReportAttachments(reportTemplateIds) {
		const promises = reportTemplateIds.map((reportTemplateId) =>
			this._makeReportAttachment(reportTemplateId),
		);

		return Promise.all(promises);
	}

	async _makeReportAttachment(reportTemplateId) {
		const reportService = new ReportService({
			period: this._periodReport.getPeriod(),
			chartOfAccounts: this._chartOfAccounts,
			keyFigures: this._keyFigures,
			company: session.getCompany(),
		});

		const reportTemplate =
			await reportService.fetchReportTemplate(reportTemplateId);

		reportTemplate
			.getDisplaySettings()
			.setAccountVisibility(
				accountRowVisibilityFactory.defaultVisibility(),
			);

		await reportService.updateReportAndData();
		const report = reportService.getReport();
		report.expandRowsUpToLevel(2);
		return report;
	}

	/**
	 * Combine multiple document data into one.
	 *
	 * @param {Array} data - Array of PDF data to be fed to pdfmake.
	 *
	 * Use the style and format of the last item of `data` for the final PDF.
	 */
	_mergePdfData(data) {
		return data.reduce(
			(result, datum) => {
				datum.content[0].pageBreak = "before";
				return {
					...datum,
					content: [...result.content, ...datum.content],
				};
			},
			{ content: [] },
		);
	}

	_makeOverlay() {
		return new PageOverlayWidget({
			translucent: false,
			contents: this._overlayContent,
		});
	}

	_renderDefaultOverlayContentOn(html) {
		return html.div(
			{ klass: "pdf-export-overlay-contents" },
			html.div(
				{ klass: "teaser" },
				fileAltIcon,
				html.span("⇒"),
				filePdfIcon,
			),
			html.h2(_("Generating the PDF file, please wait…")),
		);
	}
}
